import {useEffect, useMemo} from 'react'
import {QUERY_KEYS} from '@/queryClient'
import {generatePath} from 'react-router-dom'
import {useQueryClient} from '@tanstack/react-query'
import {AlertCircleIcon} from '@/components/ui/icon/Icon'
import {ButtonLink} from '@/components/ui/button-link/ButtonLink'
import {routes} from '@/utilities/constants/routes'
import {Button} from '@/components/ui/button/Button'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {useTranslation} from '@/translations/i18n'
import {
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    ModalTitle
} from '@/components/ui/modal-atoms/ModalAtoms'
import {ErrorBox} from '@/components/commons/error-box/ErrorBox'
import {useGetMedicalReport} from '@/features/medical-reports/queries/useGetMedicalReport'
import {MedicalReportDetailsHeading} from '@/features/medical-reports/components/medical-report-details-heading/MedicalReportDetailsHeading'
import {medicalReportKeyMapToLabel, remapMedicalReportAttributesToDefaultValues} from '@/features/medical-reports/utils'
import {MedicalReportSmall} from '@/features/medical-reports/types'
import {MedicalReportDetailsModalLoader} from './MedicalReportDetailsModalLoader'
import {useGenerateMedicalReportPdf} from '@/features/medical-reports/queries/useGenerateMedicalReportPdf'
import {useGetAttachmentSignedUrl} from '@/services/useGetAttachmentSignedUrl'
import {Skeleton} from '@/components/ui/skeleton/Skeleton'
import {Text} from '@/components/ui/text/Text'

export const MedicalReportDetailsModal = ({
    patientId,
    medicalReport,
    showEditButton,
    onClose
}: {
    patientId: string
    medicalReport: MedicalReportSmall
    showEditButton?: boolean
    onClose: () => void
}) => {
    const {t} = useTranslation()
    const medicalReportQuery = useGetMedicalReport({
        urlParams: {medicalReportId: medicalReport.id},
        params: {responseType: 'extended'}
    })
    const queryClient = useQueryClient()
    const {
        mutate: generateMedicalReportPdf,
        isPending,
        isError
    } = useGenerateMedicalReportPdf(
        {medicalReportId: medicalReport.id},
        {
            onSuccess: async () => {
                await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENT_MEDICAL_RECORD, {patientId}]})
                await queryClient.invalidateQueries({
                    queryKey: [QUERY_KEYS.MEDICAL_REPORTS, {medicalReportId: medicalReport.id}]
                })
            }
        }
    )

    const pdfAttachmentId = medicalReportQuery.data?.pdfAttachment?.id
    const signedUrlQuery = useGetAttachmentSignedUrl({
        urlParams: {attachmentId: pdfAttachmentId ?? ''},
        options: {enabled: !!pdfAttachmentId}
    })

    useEffect(() => {
        const interval = setInterval(() => {
            if (!medicalReportQuery?.data?.pdfAttachment?.id && medicalReportQuery?.data?.status == 'signed') {
                queryClient.invalidateQueries({queryKey: [QUERY_KEYS.MEDICAL_REPORTS, medicalReport.id]})
            }
        }, 5000)

        return () => clearInterval(interval)
    }, [medicalReportQuery?.data?.pdfAttachment?.id, queryClient])

    const extendedMedicalReport = useMemo(() => medicalReportQuery.data, [medicalReportQuery.data])

    const attributes = useMemo(
        () => remapMedicalReportAttributesToDefaultValues(extendedMedicalReport?.medicalRecordReportAttributes || []),
        [extendedMedicalReport?.medicalRecordReportAttributes]
    )

    return (
        <>
            <ModalOverlay onClick={onClose} />
            <Modal width="1024px" role="dialog" aria-modal>
                {medicalReportQuery.isPending ? (
                    <MedicalReportDetailsModalLoader />
                ) : medicalReportQuery.isError ? (
                    <ErrorBox icon={<AlertCircleIcon />} title={t('errors:default')} />
                ) : (
                    extendedMedicalReport && (
                        <>
                            <ModalHeader>
                                <Flexbox direction="column" gap={2}>
                                    <ModalTitle>
                                        {medicalReport.medicalRecordReportType.keyName == 'document' &&
                                        attributes.documentTitle
                                            ? attributes.documentTitle
                                            : t(
                                                  medicalReportKeyMapToLabel[
                                                      extendedMedicalReport?.medicalRecordReportType.keyName
                                                  ]
                                              )}
                                    </ModalTitle>
                                    <MedicalReportDetailsHeading
                                        date={medicalReport.date}
                                        createdByFullName={medicalReport.createdByFullName ?? '-'}
                                        specializationName={medicalReport.specializationName ?? '-'}
                                        projectCode={extendedMedicalReport?.project?.code}
                                        projectStartDate={extendedMedicalReport?.project?.startDate}
                                        isDocument={medicalReport.medicalRecordReportType.keyName == 'document'}
                                        documentDate={attributes.documentDate}
                                        documentNote={attributes.notes}
                                    />
                                </Flexbox>
                            </ModalHeader>
                            <ModalBody>
                                {medicalReportQuery.data.medicalRecordReportType.keyName == 'document' ? (
                                    <iframe width={960} height={500} src={attributes.documentAttachment?.url} />
                                ) : medicalReportQuery.data.pdfAttachment?.id ? (
                                    signedUrlQuery.isError ? (
                                        <ErrorBox icon={<AlertCircleIcon />} title={t('errors:default')} />
                                    ) : signedUrlQuery.data?.url ? (
                                        <iframe width={960} height={500} src={signedUrlQuery.data.url} />
                                    ) : (
                                        <Skeleton width={960} height={500} />
                                    )
                                ) : !medicalReportQuery.data.pdfAttachment?.id && medicalReport.status == 'signed' ? (
                                    <Flexbox align="center" gap={2}>
                                        {isError ? (
                                            <ErrorBox icon={<AlertCircleIcon />} title={t('errors:default')} />
                                        ) : (
                                            <Flexbox fullWidth gap={2} direction="column">
                                                <Text size="textSm">{t('medical_records:pdf_not_found')}</Text>
                                                <Button
                                                    variant="primaryNeutral"
                                                    onClick={() => generateMedicalReportPdf()}
                                                    disabled={isPending}
                                                >
                                                    {t('commons:generate_pdf')}
                                                    {isPending && <Spinner size={14} />}
                                                </Button>
                                            </Flexbox>
                                        )}
                                    </Flexbox>
                                ) : (
                                    <Flexbox gap={2} direction="column">
                                        <Text weight="500" size="textMd">
                                            {t('medical_records:print_preview_not_available_title')}
                                        </Text>
                                        <Text size="textSm" palette="neutral" shade="500">
                                            {t('medical_records:print_preview_not_available_paragraph')}
                                        </Text>
                                    </Flexbox>
                                )}
                            </ModalBody>
                            <ModalFooter>
                                <Flexbox justify="space-between" fullWidth>
                                    <Button onClick={onClose} variant="ghost">
                                        {t('commons:close')}
                                    </Button>
                                    <Flexbox align="center" gap={2}>
                                        {showEditButton && (
                                            <ButtonLink
                                                to={generatePath(routes.EDIT_MEDICAL_REPORT.path, {
                                                    id: patientId,
                                                    medicalReportId: medicalReport.id
                                                })}
                                                variant="secondary"
                                            >
                                                {t('commons:edit')}
                                            </ButtonLink>
                                        )}
                                        {signedUrlQuery.data?.url && (
                                            <ButtonLink
                                                variant="primaryNeutral"
                                                to={signedUrlQuery.data.url}
                                                target="_blank"
                                            >
                                                {t('commons:print')}
                                            </ButtonLink>
                                        )}
                                    </Flexbox>
                                </Flexbox>
                            </ModalFooter>
                        </>
                    )
                )}
            </Modal>
        </>
    )
}
